import styled, { createGlobalStyle } from 'styled-components'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { CellStyle } from 'ag-grid-community'

const layerToken = tokens as unknown as Record<string, string | undefined>

// ag-grid cellStyle 공유 상수 — 배열 union widening 으로 인한 CellStyle 타입 충돌 방지
export const MONO_CELL: CellStyle = { fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px' }
export const MONO_MUTED_CELL: CellStyle = { fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: '#555' }
export const BOLD_CELL: CellStyle = { fontWeight: '500' }

/* Global AG Grid cell-edit styles */
export const AgEditableCellStyles = createGlobalStyle`
  /* 편집 가능한 컬럼 커서 표시 (배경 없음 — 선택/편집 상태가 아닐 때는 흰 배경) */
  .ag-cell.editable-cell-text,
  .ag-cell.editable-cell-select {
    border-left: 1px solid #ebebeb !important;
    cursor: pointer;
  }

  /* Text cell — 인라인 편집 활성 상태 */
  .ag-cell.ag-cell-inline-editing.editable-cell-text {
    border: 1px solid #1a1a1a !important;
    background-color: rgba(36, 94, 255, 0.05) !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
  .ag-cell.ag-cell-inline-editing.editable-cell-text .ag-text-field-input,
  .ag-cell.ag-cell-inline-editing.editable-cell-text input {
    font-size: 14px !important;
    padding: 8px 12px !important;
    min-height: 36px;
    height: 100%;
    background: transparent !important;
    border: none !important;
    outline: none !important;
    color: #1a1a1a;
    caret-color: #245eff;
  }

  /* Select cell — 인라인 편집 활성 상태 */
  .ag-cell.ag-cell-inline-editing.editable-cell-select {
    border: 1px solid #1a1a1a !important;
    background-color: rgba(36, 94, 255, 0.05) !important;
    box-shadow: none !important;
    padding: 0 !important;
  }
  .ag-cell.ag-cell-inline-editing.editable-cell-select .ag-text-field-input,
  .ag-cell.ag-cell-inline-editing.editable-cell-select select {
    font-size: 14px !important;
    padding: 8px 12px !important;
    min-height: 36px;
    height: 100%;
    background: transparent !important;
    border: none !important;
    outline: none !important;
    width: 100%;
  }

  /* 라디오 컬럼 셀 가운데 정렬 */
  .moms-radio-col-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  /* ── 디자인 프로토타입 룩 (page 루트 .um-grid-skin 으로 스코프 — 다른 그리드 영향 없음) ──
     헤더 연회색 배경 + 회색 굵은 라벨, 얇은 경계선, hover/선택행 색을 프로토타입에 맞춤 */
  .um-grid-skin .ag-theme-balham {
    --ag-header-background-color: #f8f9fc;
    --ag-header-foreground-color: #555;
    --ag-row-hover-color: #fafafa;
    --ag-selected-row-background-color: #eef4ff;
    --ag-border-color: #ebebeb;
    --ag-row-border-color: #ebebeb;
    --ag-font-size: 13px;
  }
  .um-grid-skin .ag-theme-balham .ag-header-cell-text {
    font-weight: 600;
    font-size: 12px;
  }
`

export const TabWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 16px;
  --tab-line-bg-hover: transparent;
  --tab-line-bg-pressed: transparent;
`

export const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${layerToken['stroke-divider-low'] ?? '#ebebeb'};
  flex-shrink: 0;

  &,
  & * {
    font-family: 'Pretendard', sans-serif !important;
  }
`

export const UserTableWrap = styled.div`
  flex: 1;
  min-height: 360px;
  padding: 0 0 16px;
  position: relative;

  /* GridTableV2 내부 래퍼(.ag-theme-balham)를 absolute 로 박스에 고정해 채운다.
     height:100% 체인(pane→VStack→flex→100%)은 셀 편집/컬럼메뉴 리플로우 때 한 단계라도
     0 으로 잡히면 ag-grid 내부 viewport 가 0 으로 무너져(에러 없이) 그리드가 통째로 사라진다.
     absolute + top/bottom 은 부모 박스(min-height 로 바닥 보장)에 직접 고정돼 리플로우에 안 무너진다. */
  & .ag-theme-balham {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 16px;
    height: auto !important;
  }
`

/* Dialog body / footer — mwms-ds Modal 기반 (멀티화주그룹, 부서추가용) */
export const DialogBody = styled.div`
  padding: 4px 0 8px;
`

export const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 0 0;
`

/* ─── FigmaDialog styled — @musinsa/layer-components-desktop Dialog (size="small") ── */
export const FigmaDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--dialog-overlay-bg, rgba(0, 0, 0, 0.6));
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const FigmaDialogCard = styled.div`
  background: var(--dialog-bg, #ffffff);
  border-radius: 16px;
  width: 400px;
  max-width: calc(100vw - 40px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`

export const FigmaDialogTitleArea = styled.div`
  padding: 24px 24px 0;
`

export const FigmaDialogBodyArea = styled.div`
  padding: 16px 24px 0;
`

export const FigmaDialogFooterArea = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 20px 24px 24px;
`

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const FieldLabelRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

export const DisabledInput = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: ${layerToken['bg-disabled'] ?? '#f5f5f5'};
  border: 1px solid ${layerToken['stroke-input-disabled'] ?? '#e0e0e0'};
  border-radius: 6px;
  min-height: 44px;
`

export const GroupTagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 48px;
  padding: 8px 0;
`

export const GroupTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${layerToken['bg-subtle'] ?? '#f4f4f4'};
  border: 1px solid ${layerToken['stroke-divider'] ?? '#e0e0e0'};
  border-radius: 4px;
  padding: 3px 8px 3px 10px;
`

export const GroupTagRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`

export const GroupDivider = styled.div`
  border-top: 1px solid ${layerToken['stroke-divider-low'] ?? '#ebebeb'};
`

export const RadioCellWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`
